import React, { FC, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import {
  Button,
  Empty,
  List,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { translate } from '../../../core'
import { ItemType } from '../../inventory/type/item'
import {
  DeleteFilterOverride,
  GetFilterOverridesByType,
} from '../graphql/filter-config.graphql'
import { FilterOverrideAction, FilterOverrideView } from '../type/filter-config'
import { FilterOverrideDrawer } from './FilterOverrideDrawer'
import { getFilterDescriptor, productTypeDisplayLabel } from '../type/filter-catalog'

interface Props {
  productType: ItemType
}

export const FilterOverrideRulesPanel: FC<Props> = ({ productType }) => {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<FilterOverrideView | undefined>()
  const [messageApi, contextHolder] = message.useMessage()

  const { data, loading, refetch } = useQuery(GetFilterOverridesByType, {
    variables: { productType },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  })

  const [doDelete] = useMutation(DeleteFilterOverride, {
    onCompleted: () => {
      messageApi.success(translate('override_delete_success'))
      refetch()
    },
    onError: (err) => messageApi.error(err.message),
  })

  const overrides = data?.filterOverridesByType ?? []

  const onAdd = () => {
    setEditing(undefined)
    setDrawerOpen(true)
  }

  const onEdit = (entry: FilterOverrideView) => {
    setEditing(entry)
    setDrawerOpen(true)
  }

  const onClose = (changed?: boolean) => {
    setDrawerOpen(false)
    setEditing(undefined)
    if (changed) refetch()
  }

  return (
    <Spin spinning={loading && overrides.length === 0}>
      {contextHolder}
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={onAdd}>
          {translate('add_override')}
        </Button>
      </Space>
      {overrides.length === 0 ? (
        <Empty description={translate('no_overrides_yet')} />
      ) : (
        <List
          dataSource={overrides}
          renderItem={(entry) => (
            <List.Item
              key={entry.id}
              actions={[
                <Button
                  key="edit"
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => onEdit(entry)}
                />,
                <Popconfirm
                  key="delete"
                  title={translate('sure_message')}
                  onConfirm={() => doDelete({ variables: { id: entry.id } })}
                >
                  <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                title={(() => {
                  // First line = target scope path:
                  // [Product Type] → [breadcrumb…] → [Target]
                  const segments = [
                    productTypeDisplayLabel(productType),
                    ...(entry.target?.breadcrumb ?? []),
                    entry.target?.title ?? `#${entry.scopeId}`,
                  ]
                  return (
                    <Space wrap size={4} align="center">
                      {segments.map((seg, i) => (
                        <React.Fragment key={`${entry.id}-seg-${i}`}>
                          {i > 0 && (
                            <Typography.Text type="secondary">→</Typography.Text>
                          )}
                          <Tag
                            color={
                              i === 0
                                ? 'blue'
                                : i === segments.length - 1
                                  ? 'geekblue'
                                  : 'default'
                            }
                            style={{
                              marginInlineEnd: 0,
                              fontWeight: i === segments.length - 1 ? 600 : 400,
                            }}
                          >
                            {seg}
                          </Tag>
                        </React.Fragment>
                      ))}
                    </Space>
                  )
                })()}
                description={
                  <Space wrap size={6}>
                    {entry.rules.map((rule, idx) => {
                      const desc = getFilterDescriptor(rule.filterKey)
                      const label = desc?.label ?? rule.filterKey
                      return (
                        <Tag
                          key={rule.id}
                          color={
                            rule.action === FilterOverrideAction.Add
                              ? 'green'
                              : 'red'
                          }
                          style={{ marginInlineEnd: 0 }}
                        >
                          <Typography.Text
                            type="secondary"
                            style={{ fontSize: 11, marginRight: 4 }}
                          >
                            {idx + 1}.
                          </Typography.Text>
                          {rule.action === FilterOverrideAction.Add
                            ? translate('rule_add')
                            : translate('rule_remove')}
                          {': '}
                          {label}
                        </Tag>
                      )
                    })}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      )}
      {drawerOpen && (
        <FilterOverrideDrawer
          productType={productType}
          override={editing}
          onClose={onClose}
        />
      )}
    </Spin>
  )
}
